import type { Session } from '@supabase/supabase-js';
import { Card, CardContent, CardHeader, CardTitle } from '@nuberu/ui';
import { useEffect, useState, type ReactElement } from 'react';

import { ApiError } from '@/lib/api';

import { fetchPasswordAlerts, type PasswordAlert } from './fetch-password-alerts';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; alerts: PasswordAlert[] };

const whenFormat = new Intl.DateTimeFormat('es-ES', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Europe/Madrid',
});

// Passwords changed directly on Supabase, past our API, so with no check of the current one
// and no "Contraseña cambiada" email. The hourly job finds them in Supabase's audit log.
export const PasswordAlertsPanel = ({ session }: { session: Session }): ReactElement => {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    fetchPasswordAlerts(session, controller.signal)
      .then((alerts) => {
        if (!controller.signal.aborted) setState({ status: 'ready', alerts });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          status: 'error',
          message: err instanceof ApiError ? err.message : 'No pudimos cargar los avisos.',
        });
      });
    return () => controller.abort();
  }, [session]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contraseñas cambiadas fuera de la API</CardTitle>
      </CardHeader>
      <CardContent>
        {state.status === 'loading' ? (
          <p className="py-4 text-sm text-text-secondary">Cargando…</p>
        ) : state.status === 'error' ? (
          <p className="py-4 text-sm text-status-warning" role="alert">
            {state.message}
          </p>
        ) : state.alerts.length === 0 ? (
          <p className="py-4 text-sm text-text-secondary">Ninguna.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col p-0">
            {state.alerts.map((alert) => (
              <li
                key={`${alert.userId}-${alert.occurredAt}`}
                className="flex flex-col gap-0.5 border-b border-(color:--nuberu-line-soft) py-2 last:border-b-0"
              >
                <span className="text-sm font-medium text-text-primary">
                  {alert.email ?? alert.userId}
                  {alert.businessName !== null ? ` · ${alert.businessName}` : ''}
                </span>
                <span className="text-sm text-text-secondary">
                  {whenFormat.format(new Date(alert.occurredAt))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
